import { useMemo } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useChannelStateContext } from "stream-chat-react";
import { getChatMemberProfileUrl } from "Components/Chat/chatConstants";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { useDisclosedNamesMapQuery } from "Api/Queries/Social/useDisclosedNamesMapQuery";
import { useChatChannelDisplayInfo } from "Hooks/Chat/useChatChannelDisplayInfo";
import { useChatRequestActions } from "Hooks/Chat/useChatRequestActions";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat.Requests;
const COMMON_RESOURCES = Resources.Common;

export const ChatRequestActionBar = () => {
  const { channel, members } = useChannelStateContext();
  const { data, state, id } = channel;
  const { handleAccept, handleReject, handleJoinRejected, isPending } =
    useChatRequestActions({
      channelId: id,
    });
  const { t } = useResource();

  const createdBy = data?.created_by;
  const membership = state.membership;
  const { isAnonymized } = useChatChannelDisplayInfo(
    channel,
    membership?.user?.id,
  );

  // Deciding on an invite means knowing who sent it, so the requester's profile is one tap away.
  // Withheld on an anonymised channel, whose counterpart must stay unidentifiable (ADR-0008).
  const requesterProfileUrl =
    createdBy?.id && !isAnonymized
      ? getChatMemberProfileUrl(createdBy.id)
      : undefined;
  const isPendingRequest =
    membership?.invited && !membership?.invite_accepted_at;
  const isRejectedRequest =
    membership?.invited && !!membership?.invite_rejected_at;

  const { data: disclosedNames } = useDisclosedNamesMapQuery();

  // Stream carries the disclosable name only (@handle for a private sender, ADR-0007). Asking
  // disclosed the sender's real name to this viewer (App ADR-0011), so it is looked up here and
  // shown WITH the handle — "Vincent Vega (@pulped)" — so the two identities stay linked. Read
  // straight from the map rather than through useDisclosedNameResolver: that one falls back to
  // the Stream name, and here the two must stay apart. An anonymised channel keeps whatever
  // Stream says; its counterpart must stay unidentifiable.
  const senderName = useMemo(() => {
    const creator = Object.values(members ?? {}).find(
      (m) => m.user_id === createdBy?.id,
    );
    const streamName = createdBy?.name || creator?.user?.name;
    if (!streamName) {
      return t(COMMON_RESOURCES.AnonymousUser);
    }
    const disclosedName =
      !isAnonymized && createdBy?.id
        ? disclosedNames?.get(createdBy.id)
        : undefined;
    return disclosedName ? `${disclosedName} (${streamName})` : streamName;
  }, [members, createdBy, isAnonymized, disclosedNames, t]);

  if (isRejectedRequest) {
    return (
      <Stack
        py={3}
        alignItems="center"
        borderTop="1px solid"
        borderColor="divider"
      >
        <Typography fontSize={14} color="text.secondary" mb={1}>
          {t(RESOURCES.RejectedMessage)}
        </Typography>
        <Button
          size="small"
          variant="contained"
          disabled={isPending}
          onClick={handleJoinRejected}
        >
          {t(RESOURCES.Join)}
        </Button>
      </Stack>
    );
  }

  if (!isPendingRequest) {
    return null;
  }

  return (
    <Stack
      py={3}
      alignItems="center"
      borderTop="1px solid"
      borderColor="divider"
    >
      <Typography fontSize={14} color="text.secondary" mb={1}>
        {t(RESOURCES.RequestMessage, { name: senderName })}
      </Typography>
      {requesterProfileUrl && (
        <Box mb={2}>
          <UnstyledLink to={requesterProfileUrl}>
            {t(COMMON_RESOURCES.ShowProfile)}
          </UnstyledLink>
        </Box>
      )}
      <Stack direction="row" gap={2}>
        <Button
          size="small"
          variant="contained"
          disabled={isPending}
          onClick={handleAccept}
        >
          {t(RESOURCES.Accept)}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={isPending}
          onClick={handleReject}
        >
          {t(RESOURCES.Reject)}
        </Button>
      </Stack>
    </Stack>
  );
};
